import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Container,
  Chip
} from '@mui/material';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const FallbackContent = ({ category }) => {
  const fallbackArticles = {
    business: [
      {
        title: "Ethiopian Economy Shows Strong Growth in Q1 2024",
        excerpt: "The Ethiopian economy continues to demonstrate resilience with significant growth in key sectors including agriculture, manufacturing, and services.",
        date: "June 15, 2024",
        readTime: "5 min read"
      },
      {
        title: "New Investment Opportunities in Addis Ababa",
        excerpt: "The Ethiopian government announces new investment incentives for foreign and domestic investors in various sectors.",
        date: "June 14, 2024",
        readTime: "4 min read"
      },
      {
        title: "Ethiopian Airlines Expands African Network",
        excerpt: "The national carrier announces new routes and partnerships to strengthen its position as Africa's leading airline.",
        date: "June 13, 2024",
        readTime: "6 min read"
      },
      {
        title: "Digital Banking Revolution in Ethiopia",
        excerpt: "Mobile banking and digital payment systems are transforming the financial landscape across Ethiopia.",
        date: "June 12, 2024",
        readTime: "7 min read"
      }
    ],
    politics: [
      {
        title: "Ethiopian Government Announces New Policy Reforms",
        excerpt: "Comprehensive policy reforms aimed at strengthening democracy and economic development have been announced.",
        date: "June 15, 2024",
        readTime: "6 min read"
      },
      {
        title: "Regional Cooperation Strengthens in East Africa",
        excerpt: "Ethiopia plays key role in fostering regional cooperation and peace initiatives across East Africa.",
        date: "June 14, 2024",
        readTime: "5 min read"
      },
      {
        title: "Parliament Approves New Legislation",
        excerpt: "Important legislation passed to improve governance and public service delivery across the country.",
        date: "June 13, 2024",
        readTime: "4 min read"
      },
      {
        title: "International Relations: Ethiopia's Growing Global Role",
        excerpt: "Ethiopia continues to strengthen its international partnerships and diplomatic relations.",
        date: "June 12, 2024",
        readTime: "7 min read"
      }
    ],
    sports: [
      {
        title: "Ethiopian Athletes Prepare for International Competitions",
        excerpt: "National athletes are in intensive training for upcoming international competitions and championships.",
        date: "June 15, 2024",
        readTime: "5 min read"
      },
      {
        title: "Local Football League: Exciting Season Ahead",
        excerpt: "The Ethiopian Premier League promises an exciting season with new talent and competitive matches.",
        date: "June 14, 2024",
        readTime: "4 min read"
      },
      {
        title: "Youth Sports Development Program Launched",
        excerpt: "New initiative to develop young sporting talent across Ethiopia with focus on grassroots development.",
        date: "June 13, 2024",
        readTime: "6 min read"
      },
      {
        title: "Ethiopian Marathon Runners Continue Legacy",
        excerpt: "Ethiopian long-distance runners maintain their world-class performance in international marathons.",
        date: "June 12, 2024",
        readTime: "5 min read"
      }
    ]
  };

  const articles = fallbackArticles[category] || fallbackArticles.business;
  const categoryTitle = category.charAt(0).toUpperCase() + category.slice(1);

  return (
    <Container maxWidth="lg" sx={{ py: 4, mt: 10 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Typography 
          variant="h3" 
          component="h1" 
          gutterBottom 
          sx={{ 
            mb: 4, 
            fontWeight: 'bold',
            color: 'primary.main'
          }}
        >
          {categoryTitle} News
        </Typography>
        
        <Typography 
          variant="body1" 
          sx={{ 
            mb: 4, 
            color: 'text.secondary',
            fontSize: '1.1rem',
            lineHeight: 1.6
          }}
        >
          Stay informed with the latest {category} news from Ethiopia and East Africa. 
          Our dedicated team brings you comprehensive coverage of the most important developments 
          and stories that matter to our region.
        </Typography>

        <Grid container spacing={3}>
          {articles.map((article, index) => (
            <Grid xs={12} sm={6} md={4} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card 
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 3
                    }
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Box sx={{ mb: 2 }}>
                      <Chip 
                        label={categoryTitle} 
                        color="primary" 
                        size="small"
                        sx={{ mb: 1 }}
                      />
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ display: 'block' }}
                      >
                        {article.date} • {article.readTime}
                      </Typography>
                    </Box>
                    
                    <Typography 
                      variant="h6" 
                      component="h2" 
                      gutterBottom
                      sx={{ 
                        fontWeight: 'bold',
                        mb: 2,
                        lineHeight: 1.3
                      }}
                    >
                      {article.title}
                    </Typography>
                    
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ 
                        mb: 3,
                        lineHeight: 1.6
                      }}
                    >
                      {article.excerpt}
                    </Typography>
                    
                    <Button 
                      component={Link}
                      to="/"
                      variant="outlined" 
                      color="primary"
                      size="small"
                      sx={{ mt: 'auto' }}
                    >
                      Read More
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{ mb: 3, color: 'text.secondary' }}
          >
            Explore More News Categories
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            {['Politics', 'Business', 'Sports'].map((cat) => (
              <Button
                key={cat}
                component={Link}
                to={`/${cat.toLowerCase()}-news`}
                variant="contained"
                color="primary"
                sx={{ 
                  textTransform: 'none',
                  px: 3,
                  py: 1
                }}
              >
                {cat} News
              </Button>
            ))}
          </Box>
        </Box>
      </motion.div>
    </Container>
  );
};

export default FallbackContent; 